import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { Dialog, Button } from '../../../components/ui';

import {
  sendAccountDeletionOtp,
  verifyAccountDeletionOtp,
  deleteAccount,
} from '../../../services/user/Settings.services';
import { logoutApi } from '../../../services/auth/authService';
import { toast } from '../../../components/ui/notify';
import 'react-toastify/dist/ReactToastify.css';

const WARNING_COPY = (
  <>
    <p className="tw-mb-2">If you delete your account:</p>
    <ul className="tw-mb-4">
      <li>You will be logged out immediately.</li>
      <li>Your account will be disabled immediately and scheduled for permanent deletion after 60 days.</li>
      <li>You may request restoration within 60 days.</li>
      <li>After that period, your account and associated data will be permanently deleted and cannot be recovered.</li>
      <li>During this period, you will not be able to log in or use the application.</li>
    </ul>
    <p className="tw-mb-0"><strong>Are you sure you want to continue?</strong></p>
  </>
);

function DeleteAccount() {
  const [showModal, setShowModal] = useState(false);
  const [step, setStep] = useState('confirm');
  const [reason, setReason] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [deletionToken, setDeletionToken] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const resetState = () => {
    setStep('confirm');
    setReason('');
    setOtpCode('');
    setDeletionToken('');
    setIsProcessing(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetState();
  };

  const handleOpenModal = () => {
    resetState();
    setShowModal(true);
  };

  const handleSendOtp = async () => {
    setIsProcessing(true);
    try {
      const response = await sendAccountDeletionOtp();
      if (response?.success) {
        toast.success('Verification code sent to your email.');
        setStep('otp');
      } else {
        toast.error(response?.message || 'Failed to send verification code.');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send verification code.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpCode.trim()) {
      toast.error('Please enter the verification code.');
      return;
    }
    setIsProcessing(true);
    try {
      const response = await verifyAccountDeletionOtp({ otp_code: otpCode.trim() });
      if (response?.success) {
        setDeletionToken(response.data?.deletion_token || '');
        setStep('delete');
      } else {
        toast.error(response?.message || 'Invalid verification code.');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Invalid verification code.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteAccount = async () => {
    setIsProcessing(true);
    try {
      const response = await deleteAccount({
        deletion_token: deletionToken,
        deletion_reason: reason || undefined,
        confirm: true,
      });
      if (response?.success) {
        toast.success('Your account has been deleted.');
        try {
          await logoutApi();
        } catch {
          // token may already be blacklisted
        }
        localStorage.removeItem('token');
        localStorage.removeItem('role');
        localStorage.removeItem('organization_details');
        handleCloseModal();
        window.location.href = '/login';
      } else {
        toast.error(response?.message || 'Failed to delete account.');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete account.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="tw-p-0 delete-account-container">
      <div className={"tw-mb-[24px] [&_h3]:tw-text-[18px] [&_h3]:tw-font-semibold [&_h3]:tw-text-[#333] [&_h3]:tw-mb-[8px] [&_p]:tw-text-[14px] [&_p]:tw-text-[#666] [&_p]:tw-m-0 max-[768px]:[&_h3]:tw-text-[20px] section-header"}>
        <h3>Delete Account</h3>
      </div>

      <div className="tw-flex tw-bg-[#fff4f4] tw-p-[20px] tw-border-l-[4px] tw-border-solid tw-border-l-[#dc3545] tw-shadow-[0_2px_8px_rgba(0,_0,_0,_0.06)] max-[768px]:tw-flex-col max-[768px]:tw-p-[16px] warning-card tw-mb-6">
        <div className="tw-flex tw-items-start tw-mr-[16px] tw-text-[#dc3545] max-[768px]:tw-mr-0 max-[768px]:tw-mb-[12px] warning-icon">
          <Icon icon="mdi:alert-circle-outline" width="24" height="24" />
        </div>
        <div className={"[&_h4]:tw-text-[18px] [&_h4]:tw-font-semibold [&_h4]:tw-text-[#dc3545] [&_h4]:tw-mb-[12px] [&_p]:tw-text-[14px] [&_p]:tw-text-[#555] [&_p]:tw-mb-[12px] [&_p:last-child]:tw-mb-0 [&_ul]:tw-mb-[16px] [&_ul]:tw-pl-[20px] [&_li]:tw-text-[14px] [&_li]:tw-text-[#555] [&_li]:tw-mb-[6px] [&_a]:tw-text-[#3152e8] [&_a]:tw-no-underline [&_a]:tw-font-medium [&_a:hover]:tw-underline warning-content"}>
          <h4>Delete Account</h4>
          {WARNING_COPY}
        </div>
      </div>

      <div className="tw-mt-[24px] delete-account-action">
        <button type="button" className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-p-[8px_16px] tw-text-[0.875rem] tw-font-medium tw-rounded-[0.5rem] [transition:all_0.2s_ease] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[1px] tw-border-[transparent] tw-pl-4 tw-pr-4 tw-pt-2.5 tw-pb-2.5 tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [--tw-bg-opacity:1] tw-bg-[rgb(225_29_72_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(190_18_60_/_var(--tw-bg-opacity,_1))] ui-button ui-button-danger"} onClick={handleOpenModal}>
          <Icon icon="mdi:delete-outline" width="18" height="18" className="tw-me-2" />
          Delete Account
        </button>
      </div>

      <Dialog show={showModal} onHide={handleCloseModal} centered>
        <Dialog.Header closeButton>
          <Dialog.Title className="!tw-text-[var(--danger-color)] tw-text-rose-600">Delete Account</Dialog.Title>
        </Dialog.Header>
        <Dialog.Body>
          {step === 'confirm' && (
            <>
              {WARNING_COPY}
              <div className="tw-mb-4 tw-mt-4">
                <label className="tw-mb-2 tw-block tw-text-[0.875rem] tw-leading-[1.25rem] tw-font-medium [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] ui-label">Reason for leaving (optional)</label>
                <select className={"[&::placeholder]:[--tw-text-opacity:1] [&::placeholder]:tw-text-[rgb(148_163_184_/_var(--tw-text-opacity,_1))] [&:focus]:[--tw-border-opacity:1] [&:focus]:tw-border-[rgb(82_101_232_/_var(--tw-border-opacity,_1))] [&:focus]:[outline:2px_solid_transparent] [&:focus]:[outline-offset:2px] [&:focus]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus]:[--tw-ring-opacity:1] [&:focus]:[--tw-ring-color:rgb(224_231_255_/_var(--tw-ring-opacity,_1))] [&:disabled]:[--tw-bg-opacity:1] [&:disabled]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:disabled]:[--tw-text-opacity:1] [&:disabled]:tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] tw-block tw-w-full tw-rounded-[0.5rem] tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-pl-3 tw-pr-3 tw-pt-2.5 tw-pb-2.5 [--tw-text-opacity:1] tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [appearance:none] tw-pe-[2.25rem] tw-bg-[url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHdpZHRoPScyMCcgaGVpZ2h0PScyMCcgZmlsbD0nbm9uZScgc3Ryb2tlPScjNjQ3NDhiJyB2aWV3Qm94PScwIDAgMjQgMjQnPjxwYXRoIGQ9J203IDEwIDUgNSA1LTUnLz48L3N2Zz4=)] tw-bg-[right_.65rem_center] tw-bg-no-repeat [&[multiple]]:tw-bg-[none] [&.is-invalid]:[--tw-border-opacity:1] [&.is-invalid]:tw-border-[rgb(251_191_36_/_var(--tw-border-opacity,_1))] [&.is-invalid]:[--tw-ring-opacity:1] [&.is-invalid]:[--tw-ring-color:rgb(254_243_199_/_var(--tw-ring-opacity,_1))] ui-select"} value={reason} onChange={(e) => setReason(e.target.value)}>
                  <option value="">Select a reason</option>
                  <option value="not_useful">I don&apos;t find the platform useful</option>
                  <option value="too_expensive">The service is too expensive</option>
                  <option value="difficult_to_use">The platform is difficult to use</option>
                  <option value="found_alternative">I found a better alternative</option>
                  <option value="privacy_concerns">I have privacy concerns</option>
                  <option value="other">Other reason</option>
                </select>
              </div>
            </>
          )}

          {step === 'otp' && (
            <>
              <p>Enter the 6-digit code sent to your registered email address.</p>
              <input
                type="text"
                className={"[&::placeholder]:[--tw-text-opacity:1] [&::placeholder]:tw-text-[rgb(148_163_184_/_var(--tw-text-opacity,_1))] [&:focus]:[--tw-border-opacity:1] [&:focus]:tw-border-[rgb(82_101_232_/_var(--tw-border-opacity,_1))] [&:focus]:[outline:2px_solid_transparent] [&:focus]:[outline-offset:2px] [&:focus]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus]:[--tw-ring-opacity:1] [&:focus]:[--tw-ring-color:rgb(224_231_255_/_var(--tw-ring-opacity,_1))] [&:disabled]:[--tw-bg-opacity:1] [&:disabled]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:disabled]:[--tw-text-opacity:1] [&:disabled]:tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] !tw-bg-[#F9F9F9] tw-block tw-w-full tw-rounded-[0.5rem] tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-pl-3 tw-pr-3 tw-pt-2.5 tw-pb-2.5 [--tw-text-opacity:1] tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] tw-min-h-[42px] [&[type='file']]:tw-text-[0.875rem] [&[type='file']]:tw-leading-[1.25rem] [&[type='file']::file-selector-button]:tw-me-3 [&[type='file']::file-selector-button]:tw-rounded-[0.375rem] [&[type='file']::file-selector-button]:tw-border-[0px] [&[type='file']::file-selector-button]:[--tw-bg-opacity:1] [&[type='file']::file-selector-button]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&[type='file']::file-selector-button]:tw-pl-3 [&[type='file']::file-selector-button]:tw-pr-3 [&[type='file']::file-selector-button]:tw-pt-1 [&[type='file']::file-selector-button]:tw-pb-1 [&[type='file']::file-selector-button]:[--tw-text-opacity:1] [&[type='file']::file-selector-button]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] [&.is-invalid]:[--tw-border-opacity:1] [&.is-invalid]:tw-border-[rgb(251_191_36_/_var(--tw-border-opacity,_1))] [&.is-invalid]:[--tw-ring-opacity:1] [&.is-invalid]:[--tw-ring-color:rgb(254_243_199_/_var(--tw-ring-opacity,_1))] ui-input"}
                maxLength={6}
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                placeholder="123456"
              />
            </>
          )}

          {step === 'delete' && (
            <p className="tw-mb-0">
              Identity verified. Click <strong>Delete Account</strong> to complete deletion. You will be logged out immediately.
            </p>
          )}
        </Dialog.Body>
        <Dialog.Footer>
          <Button variant="outline-secondary" onClick={handleCloseModal} disabled={isProcessing}>
            Cancel
          </Button>
          {step === 'confirm' && (
            <Button variant="danger" onClick={handleSendOtp} disabled={isProcessing}>
              {isProcessing ? 'Sending...' : 'Continue'}
            </Button>
          )}
          {step === 'otp' && (
            <Button variant="danger" onClick={handleVerifyOtp} disabled={isProcessing}>
              {isProcessing ? 'Verifying...' : 'Verify code'}
            </Button>
          )}
          {step === 'delete' && (
            <Button variant="danger" onClick={handleDeleteAccount} disabled={isProcessing}>
              {isProcessing ? 'Deleting...' : 'Delete Account'}
            </Button>
          )}
        </Dialog.Footer>
      </Dialog>
    </div>
  );
}

export default DeleteAccount;
